/**
 * Agreement API helpers
 * Functions to fetch clauses and handle agreement signing
 */

import { api } from './api';  // Use configured axios instance with auth interceptor

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';

/**
 * Fetch agreement clauses for a specific state, city, and language
 * @param {string} state - State name (e.g., 'Maharashtra')
 * @param {string} city - City name (e.g., 'Mumbai')
 * @param {string} language - Language code (e.g., 'en', 'hi', 'mr')
 * @returns {Promise<Object>} Clause data with available languages
 */
export const fetchAgreementClauses = async (state, city, language = 'en') => {
  try {
    const response = await api.get(`/clauses/${state}/${city}`, {
      params: { language },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Clauses not found for this location');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch clauses');
  }
};

/**
 * Fetch all available languages
 * @returns {Promise<Array>} Array of language objects with code and name
 */
export const fetchAvailableLanguages = async () => {
  try {
    const response = await api.get(`/languages`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch languages');
  }
};

/**
 * Accept agreement terms for a specific language
 * @param {string} agreementId - Agreement ID
 * @param {string} language - Language code the user accepted terms in
 * @returns {Promise<Object>} Acceptance confirmation with timestamp
 */
export const acceptAgreementTerms = async (agreementId, language = 'en') => {
  try {
    const response = await api.post(
      `/agreements/${agreementId}/accept-terms`,
      { language },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid language selected');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to accept terms');
  }
};

/**
 * Get agreement details including current status and language
 * @param {string} agreementId - Agreement ID
 * @returns {Promise<Object>} Agreement details
 */
export const getAgreementDetails = async (agreementId) => {
  try {
    const response = await api.get(`/agreements/${agreementId}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch agreement');
  }
};

/**
 * Check if user has accepted terms for an agreement
 * @param {string} agreementId - Agreement ID
 * @returns {Promise<Object>} Acceptance status and language
 */
export const checkTermsAcceptance = async (agreementId) => {
  try {
    const agreement = await getAgreementDetails(agreementId);
    return {
      accepted: agreement.terms_accepted || false,
      language: agreement.terms_accepted_language || null,
      acceptedAt: agreement.terms_accepted_at || null,
      acceptedByRole: agreement.terms_accepted_by_role || null,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Generate/download signed agreement PDF in specified language
 * @param {string} agreementId - Agreement ID
 * @param {string} language - Language code for PDF (defaults to user's accepted language)
 * @returns {Promise<Blob>} PDF blob for download
 */
export const downloadAgreementPDF = async (agreementId, language = null) => {
  try {
    const params = language ? { language } : {};
    const response = await api.get(
      `/agreements/${agreementId}/pdf`,
      {
        params,
        responseType: 'blob',
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to download PDF');
  }
};

/**
 * Utility: Trigger PDF download in browser
 * @param {Blob} blob - PDF blob
 * @param {string} filename - Filename for download
 */
export const triggerPDFDownload = (blob, filename = 'agreement.pdf') => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Handle complete agreement signing flow:
 * 1. Fetch clauses
 * 2. Show modal for acceptance
 * 3. Accept terms
 * 4. Generate PDF
 * @param {string} agreementId - Agreement ID
 * @param {string} state - State for clauses
 * @param {string} city - City for clauses
 * @returns {Promise<Object>} Completion status
 */
export const completeAgreementSigning = async (agreementId, state, city) => {
  try {
    // Step 1: Accept terms (UI should handle this)
    const acceptanceResult = await acceptAgreementTerms(agreementId, 'en');

    // Step 2: Generate PDF
    const pdfBlob = await downloadAgreementPDF(agreementId);

    return {
      success: true,
      agreementId,
      acceptedAt: acceptanceResult.accepted_at,
      pdfBlob,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * ============ E-SIGN ENDPOINTS (NEW) ============
 */

/**
 * Get full agreement data including parties, property, and current status
 * @param {string} agreementId - Agreement ID
 * @returns {Promise<Object>} Full agreement details
 */
export const getAgreement = async (agreementId) => {
  try {
    const response = await api.get(`/agreements/${agreementId}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch agreement');
  }
};

/**
 * Save a signature drawn on canvas
 * @param {string} agreementId - Agreement ID
 * @param {string} base64Signature - Base64 PNG image from canvas
 * @param {Object} position - Signature position {x, y, width, height}
 * @returns {Promise<Object>} Signature metadata with Cloudinary URL
 */
export const drawSignature = async (agreementId, base64Signature, position = {}) => {
  try {
    const response = await api.post(
      `/signatures/draw`,
      {
        agreement_id: agreementId,
        signature_image: base64Signature,
        position: position || { x: 100, y: 450, width: 150, height: 80 },
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid signature data');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to save signature');
  }
};

/**
 * Upload a signature image file
 * @param {string} agreementId - Agreement ID
 * @param {File} signatureFile - Image file (PNG/JPG/GIF, max 5MB)
 * @param {Object} position - Signature position {x, y, width, height}
 * @returns {Promise<Object>} Signature metadata with Cloudinary URL
 */
export const uploadSignature = async (agreementId, signatureFile, position = {}) => {
  try {
    const formData = new FormData();
    formData.append('agreement_id', agreementId);
    formData.append('signature_file', signatureFile);
    formData.append('position', JSON.stringify(position || { x: 100, y: 450, width: 150, height: 80 }));

    const response = await api.post(
      `/signatures/upload`,
      formData,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid file or signature data');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to upload signature');
  }
};

/**
 * Create an E-Sign request (placeholder for Surepass NSDL integration)
 * @param {string} agreementId - Agreement ID
 * @param {string} userPhone - User's phone number for E-Sign
 * @returns {Promise<Object>} E-Sign request details
 */
export const createESignRequest = async (agreementId, userPhone) => {
  try {
    const response = await api.post(
      `/signatures/esign`,
      {
        agreement_id: agreementId,
        user_phone: userPhone,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid phone number');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to create E-Sign request');
  }
};

/**
 * Sign an agreement with signature metadata
 * @param {string} agreementId - Agreement ID
 * @param {Object} signatureData - Signature details {signature_id, name, signature_type, signature_position, signature_url}
 * @returns {Promise<Object>} Signing confirmation with timestamps
 */
export const signAgreement = async (agreementId, signatureData) => {
  try {
    const response = await api.post(
      `/agreements/${agreementId}/sign`,
      {
        agreement_id: agreementId,
        ...signatureData,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid signature data');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to sign agreement');
  }
};

/**
 * Get audit trail for agreement signing
 * @param {string} agreementId - Agreement ID
 * @returns {Promise<Array>} Array of audit trail events
 */
export const getAuditTrail = async (agreementId) => {
  try {
    const response = await api.get(
      `/agreements/${agreementId}/audit-trail`,
      {
        withCredentials: true,
      }
    );
    return response.data.audit_trail || [];
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to fetch audit trail');
  }
};

/**
 * Clear/remove a signature from agreement
 * @param {string} agreementId - Agreement ID
 * @returns {Promise<Object>} Confirmation message
 */
export const clearSignature = async (agreementId) => {
  try {
    const response = await api.delete(
      `/agreements/${agreementId}/signature`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Agreement not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to clear signature');
  }
};

/**
 * ============ PAYMENT INTEGRATION ============
 */

/**
 * Create Cashfree payment order for agreement fee
 * Routes to Orello Current Account
 * @param {string} agreementId - Agreement ID
 * @param {number} feePaisee - Amount in paise
 * @param {string} feePayer - 'tenant' | 'landlord' | 'split'
 * @returns {Promise<Object>} Payment order with session_id and payment_url
 */
export const createAgreementFeeOrder = async (agreementId, feePaise, feePayer = 'tenant') => {
  try {
    const response = await api.post(
      `/agreements/payments/agreement-fee/order`,
      {
        agreement_id: agreementId,
        amount_paise: feePaise,
        fee_payer: feePayer,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid payment data');
    }
    throw new Error(error.response?.data?.detail || 'Failed to create payment order');
  }
};

/**
 * Create Cashfree payment order for deposit protection fee
 * Routes to Orello Current Account (85%) + Broker Wallet (15%)
 * @param {string} agreementId - Agreement ID
 * @param {number} depositAmount - Deposit amount in rupees
 * @returns {Promise<Object>} Payment order with session_id and payment_url
 */
export const createDepositProtectionFeeOrder = async (agreementId, depositAmount) => {
  try {
    const response = await api.post(
      `/agreements/payments/protection-fee/order`,
      {
        agreement_id: agreementId,
        deposit_amount: depositAmount,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid payment data');
    }
    throw new Error(error.response?.data?.detail || 'Failed to create payment order');
  }
};

/**
 * Create Cashfree payment order for security deposit
 * Routes to Orello Escrow Account (secure hold for tenant)
 * @param {string} agreementId - Agreement ID
 * @param {number} depositAmountPaise - Deposit amount in paise
 * @returns {Promise<Object>} Payment order with session_id and payment_url
 */
export const createDepositPaymentOrder = async (agreementId, depositAmountPaise) => {
  try {
    const response = await api.post(
      `/agreements/payments/deposit/order`,
      {
        agreement_id: agreementId,
        amount_paise: depositAmountPaise,
      },
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.detail || 'Invalid payment data');
    }
    throw new Error(error.response?.data?.detail || 'Failed to create payment order');
  }
};

/**
 * Redirect to Cashfree payment checkout
 * @param {Object} paymentOrder - Payment order from createPaymentOrder
 * @param {string} paymentOrder.payment_url - Cashfree checkout URL
 */
export const redirectToPayment = (paymentOrder) => {
  if (!paymentOrder || !paymentOrder.payment_url) {
    throw new Error('Invalid payment order');
  }
  window.location.href = paymentOrder.payment_url;
};

/**
 * Check payment status after user returns from Cashfree
 * @param {string} orderId - Cashfree order ID
 * @returns {Promise<Object>} Payment status
 */
export const checkPaymentStatus = async (orderId) => {
  try {
    const response = await api.get(
      `/agreements/payments/status/${orderId}`,
      {
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized - please login again');
    }
    if (error.response?.status === 404) {
      throw new Error('Payment order not found');
    }
    throw new Error(error.response?.data?.detail || 'Failed to check payment status');
  }
};
